export default function usePostcodeDropdown(countries, country, onCountryChange) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const dropdownRef = useRef(null);

  // Put GB first if it exists in the countries list
  const gbIndex = countries.findIndex(c => c.ISO === 'GB');
  const sortedCountries = gbIndex !== -1 
    ? [countries[gbIndex], ...countries.filter(c => c.ISO !== 'GB')]
    : countries;

  // Find matching country based on search term
  // Exclude currently selected country (help users find other countries)
  const matchingCountry = searchTerm 
    ? sortedCountries.find(countryData =>
        countryData.ISO !== country && 
        countryData.Country.toLowerCase().startsWith(searchTerm.toLowerCase())
      )
    : null;

  const handleCountryChange = (selectedCountry) => {
    onCountryChange(selectedCountry);
    setIsDropdownOpen(false);
    setSearchTerm('');

    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
  };

  const toggleDropdown = (disabled = false) => {
    if (!disabled) {
      setIsDropdownOpen(!isDropdownOpen);
      setSearchTerm('');
    }
  };

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard search and escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isDropdownOpen && event.key.length === 1) {
        if (searchTimeout) {
          clearTimeout(searchTimeout);
        }
        
        setSearchTerm(prev => prev + event.key);
        
        const newTimeout = setTimeout(() => {
          setSearchTerm('');
        }, 1000);
        
        setSearchTimeout(newTimeout);
      } else if (isDropdownOpen && event.key === 'Backspace') {
        setSearchTerm(prev => prev.slice(0, -1));
      } else if (event.key === 'Escape') {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen, searchTimeout]);

  // Auto-scroll to matching country when searching
  useEffect(() => {
    if (matchingCountry && isDropdownOpen && dropdownRef.current) {
      const countryElement = dropdownRef.current.querySelector(`[data-country-iso="${matchingCountry.ISO}"]`);
      const dropdownList = dropdownRef.current.querySelector('.country-list');
      
      if (countryElement && dropdownList) {
        const elementTop = countryElement.offsetTop;
        const elementHeight = countryElement.offsetHeight;
        const dropdownHeight = dropdownList.offsetHeight;
        
        // Calculate scroll position to center the element in the dropdown
        const scrollTop = elementTop - (dropdownHeight / 2) + (elementHeight / 2);
        
        dropdownList.scrollTop = scrollTop;
      }
    }
  }, [matchingCountry, isDropdownOpen]);

  // Auto-scroll to selected country when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && dropdownRef.current) {
      const timeoutId = setTimeout(() => {
        const selectedCountryElement = dropdownRef.current.querySelector(`[data-country-iso="${country}"]`);
        const dropdownList = dropdownRef.current.querySelector('.country-list');
        
        if (selectedCountryElement && dropdownList) {
          const elementTop = selectedCountryElement.offsetTop;
          const elementHeight = selectedCountryElement.offsetHeight;
          const dropdownHeight = dropdownList.offsetHeight;
          
          // Position selected country at the end of the visible area
          const scrollTop = elementTop - dropdownHeight + elementHeight;
          
          dropdownList.scrollTop = Math.max(0, scrollTop);
        }
      }, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isDropdownOpen, country]);

  return {
    isDropdownOpen,
    dropdownRef,
    sortedCountries,
    matchingCountry,
    handleCountryChange,
    toggleDropdown
  };
}
